// The access and reset tokens are in-memory only — never localStorage or sessionStorage
// (ADR-0005). The refresh token is the documented exception; see `getRefreshToken` below for why
// it is persisted and why the storage is session-scoped.
const REFRESH_TOKEN_KEY = 'fieldpro.refreshToken';

let accessToken: string | null = null;
let refreshToken: string | null = null;
let resetToken: string | null = null;

/**
 * Storage access, guarded.
 *
 * `sessionStorage` throws rather than returning null when a browser blocks storage outright
 * (Safari's Lockdown Mode, a hardened enterprise profile, some private-window configurations). A
 * grower in that browser should lose reload-survival, not the ability to sign in at all, so every
 * access degrades to the in-memory behaviour instead of propagating.
 */
function readStoredRefreshToken(): string | null {
  try {
    return sessionStorage.getItem(REFRESH_TOKEN_KEY);
  } catch {
    return null;
  }
}

function writeStoredRefreshToken(token: string | null): void {
  try {
    if (token) sessionStorage.setItem(REFRESH_TOKEN_KEY, token);
    else sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch {
    // Storage unavailable — the in-memory value above still serves this page's lifetime.
  }
}

export const tokenStore = {
  getAccessToken: (): string | null => accessToken,
  setAccessToken: (token: string | null): void => {
    accessToken = token;
  },
  /**
   * The rotating refresh token — the one credential that is persisted, and the only one.
   *
   * ADR-0005 assumed this would never reach JavaScript: the API would set an HttpOnly `Secure`
   * cookie and the browser would replay it. The deployed API does not. It takes the token in the
   * **body** of `POST /auth/tracking/refresh` and hands back a new one each time; probed
   * 2026-09-02, an empty body answers
   * `400 VALIDATION_FAILED { field: 'refreshToken', message: 'refreshToken should not be empty' }`,
   * and nothing on the realm sets a cookie. So the client has to hold it, and holding it in memory
   * alone meant every page reload signed the grower out — there was no credential left to recover
   * the session with.
   *
   * **`sessionStorage`, deliberately, not `localStorage`.** It survives a reload and same-tab
   * navigation, and is dropped the moment the tab closes, so the token's life is the browsing
   * session rather than the machine's. The access token stays in memory regardless.
   *
   * The trade-off this accepts, stated plainly: a script running on this origin can read the token,
   * so an XSS becomes a session takeover rather than a page defacement. That is the cost of the
   * body-based contract, and it goes away the day the backend issues the cookie ADR-0005 describes.
   */
  getRefreshToken: (): string | null => {
    // Read through to storage rather than trusting the module variable: on a reload this module is
    // re-evaluated with `refreshToken` back at null, and storage is the only thing that survived.
    if (refreshToken) return refreshToken;
    refreshToken = readStoredRefreshToken();
    return refreshToken;
  },
  setRefreshToken: (token: string | null): void => {
    refreshToken = token;
    writeStoredRefreshToken(token);
  },
  getResetToken: (): string | null => resetToken,
  setResetToken: (token: string | null): void => {
    resetToken = token;
  },
  clear: (): void => {
    accessToken = null;
    refreshToken = null;
    resetToken = null;
    // Storage too: a sign-out that left the token behind would let the next person in this tab
    // recover the previous grower's session.
    writeStoredRefreshToken(null);
  },
};
